import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, RouterStateSnapshot } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { AuthService } from '@ortho-next/auth-core';
import { Observable, of } from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';
import { DEFAULT_APP_VERSION } from './auth.config';
import { APP_CONFIG } from './core';
import { UserService } from './services';
import { ConfirmationComponent } from './shared';


@Injectable({ providedIn: 'root' })
export class AppVersionGuard implements CanActivate {

	private readonly modalSrv = inject(NgbModal);
	private readonly configs = inject(APP_CONFIG);
	private readonly userSrv = inject(UserService);
	private readonly authSrv = inject(AuthService);


	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		return this.authSrv.isDoneLoading$.pipe(
			filter(() => this.authSrv.hasValidToken()),
			map(() => this.authSrv.applicationVersion || DEFAULT_APP_VERSION),
			map(appVer => appVer == this.configs.appVersion),
			mergeMap(isValid => isValid ? of({ isValid, url: null }) : this.userSrv.getApplicationUrl().pipe(map(url => ({ isValid, url })))),
			tap(({ isValid, url }) => !isValid && this.openUnauthorizedModal(url)),
			map(({ isValid }) => isValid)
		);
	}

	openUnauthorizedModal(url: string): void {
		const modalRef: NgbModalRef = this.modalSrv.open(ConfirmationComponent, { centered: true, backdrop: 'static' });
		(modalRef.componentInstance as ConfirmationComponent).config = {
			title: 'Warning',
			message: `
			You don't have access to this version of the application. <br/>
			Your account is authorized for <b>version ${this.authSrv.applicationVersion || DEFAULT_APP_VERSION} </b>. <br/>
			Clicking CONTINUE, you will be redirected to the correct application.
			`,
			confirmBtn: 'CONTINUE',
			cancelBtn: 'LOG OUT'
		};
		modalRef.result.then(
			() => globalThis.location.href = url,
			() => this.authSrv.logout(false, url)
		).catch(error => false);
	}

}